// Deepgram Nova-3 real-time streaming. Audio captured via pcmCapture.worklet.js,
// downsampled to session target rate (8/16 kHz), Int16 PCM-encoded, streamed over a WS opened with a
// short-lived token from /api/asr/token. Phase 1 robustness pass (2026-05-29)
// adds auto-reconnect, diagnostics counters, and a Sentry-shaped console.warn on
// unexpected close. See docs/planning/deepgram_streaming_robustness.md.

import {
  applyPcmFrameResult,
  attachDeepgramAudioHandlers,
  createDeepgramAudioGraph,
  disconnectDeepgramAudioGraph,
  loadDeepgramCaptureWorklet,
  tryLoadDeepgramResampleWorklet,
  type DeepgramAudioGraphNodes,
} from "./deepgramAudioGraph";
import {
  buildUnexpectedCloseLog,
  emptyDiagnostics,
  readEffectiveType,
  recordDeepgramUnexpectedClose,
  type DeepgramDiagnostics,
} from "./deepgramDiagnostics";
import { parseDeepgramResultsFrame } from "./deepgramMessageParse";
import {
  createKeepaliveTimer,
  DEEPGRAM_KEEPALIVE_JSON,
  shouldSendKeepAlive,
  type KeepaliveTimerHandle,
} from "./deepgramKeepalive";
import { MAX_RECONNECT_ATTEMPTS, nextReconnectDelayMs, resolveReconnectToken } from "./deepgramReconnect";
import { fetchDeepgramToken } from "./deepgramTokenFetch";
import { buildDeepgramListenQuery } from "./deepgramListenQuery";
import { selectPcmSampleRateHz } from "./deepgramSampleRate";
import { recoverDeepgramAudioAfterDeviceLoss } from "./deepgramAudioRecovery";
import { DeepgramDeviceMonitor } from "./deepgramDeviceMonitor";

const DG_HOST = "wss://api.deepgram.com/v1/listen";

/** Compatible with LiveAsrEngineDeps — extra fields are accepted but unused. */
export type DeepgramEngineDeps = {
  onStatus?: (s: string) => void;
  onAppendText: (chunk: string) => void;
  log?: (...args: any[]) => void;
  minWindowRms?: number; // accepted for API compatibility; not used
  provider?: unknown;    // accepted for API compatibility; not used
};

export class DeepgramStreamingEngine {
  private readonly deps: DeepgramEngineDeps;
  private readonly log: (...args: any[]) => void;

  private modelReady = false;
  private running = false;

  private stream: MediaStream | null = null;
  private audioCtx: AudioContext | null = null;
  private audioGraph: DeepgramAudioGraphNodes | null = null;
  private resampleWorkletLoaded = false;
  private ws: WebSocket | null = null;
  private cachedToken: string | null = null;
  private lastPcmSentAtMs = 0;
  private keepaliveTimer: KeepaliveTimerHandle | null = null;
  private sessionPcmSampleRateHz = 16_000;
  private readonly deviceMonitor = new DeepgramDeviceMonitor();
  private recoveringAudio = false;

  private transcript = "";

  private diagnostics: DeepgramDiagnostics = emptyDiagnostics();

  constructor(deps: DeepgramEngineDeps) {
    this.deps = deps;
    this.log = deps.log ?? (() => undefined);
  }

  getTranscript(): string {
    return this.transcript;
  }

  isInitialized(): boolean {
    return this.modelReady;
  }

  getDiagnostics(): DeepgramDiagnostics {
    return { ...this.diagnostics };
  }

  reset(): void {
    this.transcript = "";
  }

  async initModel(): Promise<void> {
    this.modelReady = true;
    this.deps.onStatus?.("Ready");
  }

  private async ensureAudioGraph(): Promise<void> {
    if (this.audioCtx && this.audioCtx.state !== "closed") return;

    this.audioCtx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();

    await loadDeepgramCaptureWorklet(this.audioCtx);
    this.resampleWorkletLoaded = await tryLoadDeepgramResampleWorklet(this.audioCtx);

    this.log("[deepgram] audioCtx sampleRate:", this.audioCtx.sampleRate, {
      resampleWorkletLoaded: this.resampleWorkletLoaded,
    });
  }

  private async startAudio(): Promise<void> {
    const stream = this.stream ?? (await navigator.mediaDevices.getUserMedia({ audio: true }));
    await this.wireAudioGraphFromStream(stream);
    this.modelReady = true;
  }

  private async wireAudioGraphFromStream(stream: MediaStream): Promise<void> {
    this.stream = stream;
    await this.ensureAudioGraph();

    if (this.audioCtx!.state === "suspended") {
      await this.audioCtx!.resume();
    }

    this.audioGraph = await createDeepgramAudioGraph({
      audioCtx: this.audioCtx!,
      stream: this.stream,
      targetSampleRateHz: this.sessionPcmSampleRateHz,
      resampleWorkletAvailable: this.resampleWorkletLoaded,
      log: (...args) => this.log(...args),
    });
    this.diagnostics.audioPath = this.audioGraph.audioPath;

    attachDeepgramAudioHandlers({
      nodes: this.audioGraph,
      getWs: () => this.ws,
      isRunning: () => this.running,
      targetSampleRateHz: this.sessionPcmSampleRateHz,
      sink: {
        onFrameResult: (result) => {
          applyPcmFrameResult(this.diagnostics, result, () => {
            this.lastPcmSentAtMs = Date.now();
          });
        },
      },
    });

    this.attachDeviceMonitor();
  }

  private attachDeviceMonitor(): void {
    this.deviceMonitor.attach({
      getStream: () => this.stream,
      isRunning: () => this.running,
      onDeviceLost: (reason) => {
        void recoverDeepgramAudioAfterDeviceLoss(this.audioRecoveryDeps(), reason);
      },
      log: (...args) => this.log(...args),
    });
    this.deviceMonitor.bindTrack(this.stream?.getAudioTracks()[0] ?? null);
  }

  private audioRecoveryDeps() {
    return {
      isRunning: () => this.running,
      isRecovering: () => this.recoveringAudio,
      setRecovering: (value: boolean) => {
        this.recoveringAudio = value;
      },
      onStatus: this.deps.onStatus,
      log: (...args: unknown[]) => this.log(...args),
      teardownAudioGraph: () => this.teardownAudioGraph(),
      wireAudioGraphFromStream: (stream: MediaStream) => this.wireAudioGraphFromStream(stream),
      isWsOpen: () => this.ws?.readyState === WebSocket.OPEN,
    };
  }

  private teardownAudioGraph(): void {
    this.deviceMonitor.detach();
    disconnectDeepgramAudioGraph(this.audioGraph);
    this.audioGraph = null;
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  /** Used by both `start()` and `tryReconnect()`. Audio graph survives reconnects. */
  private openWsAndAttach(token: string, isReconnect: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const query = buildDeepgramListenQuery(this.sessionPcmSampleRateHz);
      const url = `${DG_HOST}?${query}`;
      this.log("[deepgram] connecting:", url, { isReconnect, sampleRateHz: this.sessionPcmSampleRateHz });
      const ws = new WebSocket(url, ["token", token]);
      ws.binaryType = "arraybuffer";
      this.ws = ws;
      this.diagnostics.lastConnectAttemptAtMs = Date.now();

      ws.onopen = () => {
        this.log("[deepgram] WS open", { isReconnect });
        this.diagnostics.lastConnectedAtMs = Date.now();
        this.lastPcmSentAtMs = Date.now();
        this.startKeepaliveTimer();
        this.deps.onStatus?.("Listening…");
        if (isReconnect) {
          // Audio graph stays alive across reconnects. Counter resets on
          // first inbound Results frame so a silent half-open WS still
          // counts against the retry budget.
          this.diagnostics.totalReconnectsSucceeded += 1;
          resolve();
        } else {
          this.startAudio().then(resolve).catch((err) => {
            try { ws.close(1011, "audio setup failed"); } catch { /* intentional */ }
            this.ws = null;
            reject(err);
          });
        }
      };

      ws.onerror = () => {
        reject(new Error("Deepgram WebSocket connection failed"));
      };

      ws.onclose = (ev) => {
        this.handleWsClose(ev);
      };

      ws.onmessage = (ev) => {
        if (typeof ev.data !== "string") return;
        const parsed = parseDeepgramResultsFrame(ev.data);
        if (!parsed) return;
        this.diagnostics.lastResultAtMs = Date.now();
        if (this.diagnostics.reconnectAttempts > 0) {
          this.log("[deepgram] reconnect verified by Results frame; resetting attempt counter");
          this.diagnostics.reconnectAttempts = 0;
        }
        this.transcript = this.transcript ? this.transcript + " " + parsed.text : parsed.text;
        this.deps.onAppendText(parsed.text);
        this.log("[deepgram] final:", parsed.text);
      };
    });
  }

  /** Branch off `ws.onclose` — clean stops go silent, unexpected closes kick a reconnect. */
  private handleWsClose(ev: CloseEvent): void {
    this.stopKeepaliveTimer();
    this.log("[deepgram] WS closed", { code: ev.code, reason: ev.reason });
    this.diagnostics.lastCloseCode = ev.code;
    this.diagnostics.lastCloseReason = ev.reason ?? "";

    if (!this.running) return;
    if (ev.code === 1000) {
      this.running = false;
      this.deps.onStatus?.("Disconnected – tap to reconnect");
      return;
    }

    const payload = buildUnexpectedCloseLog({
      code: ev.code,
      reason: ev.reason,
      diagnostics: this.diagnostics,
      nowMs: Date.now(),
    });
    console.warn("[deepgram] unexpected close", payload);
    recordDeepgramUnexpectedClose({ payload, diagnostics: this.diagnostics });
    void this.tryReconnect();
  }

  /** Loop reconnect attempts with the locked backoff schedule. */
  private async tryReconnect(): Promise<void> {
    while (this.running) {
      this.diagnostics.reconnectAttempts += 1;
      const attempt = this.diagnostics.reconnectAttempts;
      if (attempt > MAX_RECONNECT_ATTEMPTS) {
        this.running = false;
        this.deps.onStatus?.("Reconnecting failed – tap mic to retry.");
        return;
      }
      const delay = nextReconnectDelayMs(attempt);
      this.deps.onStatus?.(`Reconnecting (attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS})…`);
      await new Promise<void>((res) => setTimeout(res, delay));
      if (!this.running) return;
      try {
        const token = await resolveReconnectToken({
          lastCloseCode: this.diagnostics.lastCloseCode,
          cachedToken: this.cachedToken,
          fetchToken: fetchDeepgramToken,
        });
        this.cachedToken = token;
        await this.openWsAndAttach(token, /* isReconnect */ true);
        return;
      } catch (e) {
        this.log("[deepgram] reconnect attempt failed", e);
      }
    }
  }

  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;

    this.diagnostics = emptyDiagnostics();
    this.diagnostics.sessionStartedAtMs = Date.now();
    this.diagnostics.effectiveType = readEffectiveType();
    this.sessionPcmSampleRateHz = selectPcmSampleRateHz(this.diagnostics.effectiveType);
    this.diagnostics.pcmSampleRateHz = this.sessionPcmSampleRateHz;
    this.log("[deepgram] session PCM sample rate:", this.sessionPcmSampleRateHz, {
      effectiveType: this.diagnostics.effectiveType,
    });

    this.deps.onStatus?.("Connecting…");

    try {
      const token = await fetchDeepgramToken();
      this.cachedToken = token;
      await this.openWsAndAttach(token, /* isReconnect */ false);
    } catch (e) {
      this.running = false;
      const msg = e instanceof Error ? e.message : String(e);
      this.log("[deepgram] start failed:", msg);
      this.deps.onStatus?.("Failed to start – check config");
      throw e;
    }
  }

  async stop(): Promise<void> {
    if (!this.running) return;
    this.running = false;
    this.stopKeepaliveTimer();

    this.deps.onStatus?.("Stopping…");

    this.teardownAudioGraph();

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: "CloseStream" }));
        // Allow a longer window for Deepgram to flush buffered speech
        // before closing — 1.2s covers most late is_final results.
        await new Promise<void>((res) => setTimeout(res, 1200));
      } catch { /* intentional */ }
      try { this.ws.close(1000, "session ended"); } catch { /* intentional */ }
      this.ws = null;
    }

    this.deps.onStatus?.("Stopped");
  }

  destroy(): void {
    this.modelReady = false;
    this.running = false;
    this.stopKeepaliveTimer();
    this.cachedToken = null;
    this.deviceMonitor.detach();
    this.teardownAudioGraph();

    try { this.audioCtx?.close(); } catch { /* intentional */ }
    this.audioCtx = null;

    if (this.ws) {
      try { this.ws.close(); } catch { /* intentional */ }
      this.ws = null;
    }
  }

  private startKeepaliveTimer(): void {
    this.stopKeepaliveTimer();
    this.keepaliveTimer = createKeepaliveTimer(() => this.tickKeepalive());
    this.keepaliveTimer.start();
  }

  private stopKeepaliveTimer(): void {
    this.keepaliveTimer?.stop();
    this.keepaliveTimer = null;
  }

  private tickKeepalive(): void {
    if (!this.running || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (!shouldSendKeepAlive(this.lastPcmSentAtMs, now)) return;
    try {
      this.ws.send(DEEPGRAM_KEEPALIVE_JSON);
      this.diagnostics.keepalivesSent += 1;
      this.log("[deepgram] KeepAlive sent", { keepalivesSent: this.diagnostics.keepalivesSent });
    } catch (e) {
      this.log("[deepgram] KeepAlive send failed", e);
    }
  }
}
